import { Buttons, CancelButton, Checkbox, DateTime, DeleteButton, Level, ModalCard } from '~/components';
import { useApi, useIntegrations, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import React, { useState } from 'react';
import styled from 'styled-components';

const P = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

function DeleteInvoiceDialog({ invoice, onDelete, onClose }) {
  const { workspace } = useWorkspace();
  const integrations = useIntegrations();
  const [removeFromQuickBooks, setRemoveFromQuickBooks] = useState(false);
  const [removeFromXero, setRemoveFromXero] = useState(false);
  const [{ isSubmitting }, form] = useForm();
  const toast = useToast();
  const api = useApi();

  const handleSubmit = async () => {
    form.submit();

    if (removeFromQuickBooks) {
      try {
        await api.www.workspaces(workspace.id).invoices(invoice.id).removeFromQuickBooks();
      } catch (error) {
        toast.error(error.message ?? 'An error has occurred. The invoice has not been removed from QuickBooks.');
      }
    } else if (removeFromXero) {
      try {
        await api.www.workspaces(workspace.id).invoices(invoice.id).removeFromXero();
      } catch (error) {
        toast.error(error.message ?? 'An error has occurred. The invoice has not been removed from Xero.');
      }
    }

    await api.www.workspaces(workspace.id).invoices(invoice.id).delete();

    if (onDelete) await onDelete();
    form.done();
    toast.success(`Invoice #${invoice.number} has been deleted.`);
  };

  const title = invoice.status.id === 'draft' ? 'Delete Draft Invoice' : 'Delete Published Invoice';

  return (
    <ModalCard title={title} onClose={onClose}>
      <ModalCard.Body>
        <P>
          Are you sure you want to delete {invoice.status.id === 'draft' ? 'draft' : 'published'} invoice #
          {invoice.number} for the <strong>{invoice.client.name}</strong> client? Any hours and expenses associated with
          this invoice will return to the Ready to Bill screen.
        </P>
        {invoice.sentAt && (
          <P>
            This invoice was sent to the client on <DateTime value={invoice.sentAt} />.
          </P>
        )}
      </ModalCard.Body>

      <ModalCard.Footer>
        <Level>
          {integrations.qbo && invoice.qboInvoiceId && (
            <Level.Item>
              <Checkbox
                label="Remove from QuickBooks"
                checked={removeFromQuickBooks}
                onChange={() => setRemoveFromQuickBooks(!removeFromQuickBooks)}
              />
            </Level.Item>
          )}

          {!!integrations.xero && invoice.xeroInvoiceId && (
            <Level.Item>
              <Checkbox
                label="Remove from Xero"
                subText={invoice.xeroConnection?.name}
                checked={removeFromXero}
                onChange={() => setRemoveFromXero(!removeFromXero)}
              />
            </Level.Item>
          )}

          <Level.Item>
            <Buttons align="right">
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <DeleteButton isLoading={isSubmitting} onClick={handleSubmit}>
                Delete
              </DeleteButton>
            </Buttons>
          </Level.Item>
        </Level>
      </ModalCard.Footer>
    </ModalCard>
  );
}

export default DeleteInvoiceDialog;
